import React, {
  useCallback, useEffect, useRef, useState
} from 'react';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';
import { handleDragStart } from 'arts/components/slider_arrows';
import left from './left.svg';
import right from './right.svg';
import { trackEvent } from '../../config/analytics';

const AUTOPLAY_INTERVAL = 5000;
const SWIPE_THRESHOLD = 40;
// a slide whose assets never decode must not stall the carousel
const DECODE_TIMEOUT = 300;

const EASE_MEDIA = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
const EASE_TEXT = 'cubic-bezier(0.215, 0.61, 0.355, 1)';
const EASE_LEAVE = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';

export const renderPicture = ({ sources, img }, name) => (
  <picture>
    {
      sources.map(
        (item, index) => <source key={ index } { ...item } />
      )
    }
    <img { ...img } alt={ name } />
  </picture>
);

// every slide is a photo <picture> followed by the SVG overlay holding title + CTA
const imagesOf = (slide) => {
  const imgs = slide ? slide.querySelectorAll('picture img') : [];
  return { photo: imgs[0] || null, overlay: imgs[1] || null };
};

const reducedMotion = () => (
  !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const decoded = (slide) => {
  const imgs = Array.from(slide ? slide.querySelectorAll('img') : []);
  return Promise.race([
    Promise.allSettled(imgs.map((img) => (img.decode ? img.decode() : Promise.resolve()))),
    new Promise((resolve) => { setTimeout(resolve, DECODE_TIMEOUT); })
  ]);
};

const leaveSequence = (slide) => {
  const { photo, overlay } = imagesOf(slide);
  const animations = [];
  if (overlay) {
    animations.push(overlay.animate(
      {
        opacity: [1, 0], transform: ['translateY(0)', 'translateY(10px)']
      },
      { duration: 250, easing: EASE_LEAVE, fill: 'both' }
    ));
  }
  if (photo) {
    animations.push(photo.animate(
      { opacity: [1, 0] },
      {
        duration: 200, delay: 150, easing: EASE_LEAVE, fill: 'both'
      }
    ));
  }
  return animations;
};

const enterSequence = (slide) => {
  const { photo, overlay } = imagesOf(slide);
  const animations = [slide.animate(
    { opacity: [0, 1] },
    { duration: 800, easing: EASE_MEDIA, fill: 'both' }
  )];
  if (photo) {
    animations.push(photo.animate(
      {
        opacity: [0, 1], transform: ['scale(1.2)', 'scale(1)']
      },
      { duration: 800, easing: EASE_MEDIA, fill: 'both' }
    ));
  }
  if (overlay) {
    animations.push(overlay.animate(
      {
        opacity: [0, 1], transform: ['translateY(30px)', 'translateY(0)']
      },
      {
        duration: 600, delay: 400, easing: EASE_TEXT, fill: 'both'
      }
    ));
  }
  return animations;
};

const settled = (animations) => Promise.all(
  animations.map((animation) => animation.finished.catch(() => {}))
);

// drops the fill:both end states so the slide falls back to its CSS resting style
const clearAnimations = (slide) => {
  if (slide && slide.getAnimations) {
    slide.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
  }
};

// these are 2560px assets: only hint the compositor while they actually move
const setWillChange = (slide, value) => {
  const { photo, overlay } = imagesOf(slide);
  [photo, overlay].forEach((img) => {
    if (img) {
      // eslint-disable-next-line no-param-reassign
      img.style.willChange = value;
    }
  });
};

const HpPromoV2 = ({ config, showArrows, interval }) => {
  const count = config.length;
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(0);
  const [autoplayTick, setAutoplayTick] = useState(0);
  const slides = useRef([]);
  const selectedRef = useRef(0);
  const busy = useRef(false);
  const swipe = useRef({ x: 0, suppressClick: false });

  const goTo = useCallback((next) => {
    if (busy.current || count < 2) {
      return;
    }
    setIndex(((next % count) + count) % count);
    setAutoplayTick((tick) => tick + 1);
  }, [count]);

  useEffect(() => {
    trackEvent('home page');
  }, []);

  useEffect(() => {
    if (count < 2) {
      return undefined;
    }
    const id = setInterval(() => {
      if (!busy.current) {
        setIndex((current) => (current + 1) % count);
      }
    }, interval);
    return () => clearInterval(id);
  }, [count, interval, autoplayTick]);

  // leave -> decode -> swap -> enter, run strictly in sequence rather than crossfaded
  useEffect(() => {
    if (index === selectedRef.current) {
      return undefined;
    }
    const from = slides.current[selectedRef.current];
    const to = slides.current[index];
    selectedRef.current = index;

    if (!to || reducedMotion()) {
      clearAnimations(from);
      setSelected(index);
      return undefined;
    }

    let cancelled = false;
    busy.current = true;

    (async () => {
      setWillChange(from, 'transform, opacity');
      await settled(leaveSequence(from));
      if (cancelled) {
        return;
      }

      await decoded(to);
      if (cancelled) {
        return;
      }

      setWillChange(to, 'transform, opacity');
      // the class swap has to land before the enter keyframes start
      flushSync(() => setSelected(index));
      clearAnimations(from);
      setWillChange(from, '');

      await settled(enterSequence(to));
      if (cancelled) {
        return;
      }

      clearAnimations(to);
      setWillChange(to, '');
      busy.current = false;
    })();

    return () => {
      cancelled = true;
      busy.current = false;
      [from, to].forEach((slide) => {
        clearAnimations(slide);
        setWillChange(slide, '');
      });
    };
  }, [index]);

  const onTouchStart = (event) => {
    swipe.current = { x: event.touches[0].clientX, suppressClick: false };
  };

  const onTouchEnd = (event) => {
    const delta = event.changedTouches[0].clientX - swipe.current.x;
    if (Math.abs(delta) < SWIPE_THRESHOLD) {
      return;
    }
    swipe.current.suppressClick = true;
    goTo(delta < 0 ? index + 1 : index - 1);
  };

  // a swipe must not follow the slide's link
  const onClickCapture = (event) => {
    if (swipe.current.suppressClick) {
      swipe.current.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // react-alice-carousel no longer drives this hero, but its class names stay: the lib CSS
  // is still imported globally (arts/index.js), hpv2.scss positions the arrows against the
  // .alice-carousel root, and _v2_home_page.html.haml server-renders this exact tree for
  // slide 0. Renaming any of it costs the arrow placement and a hydration shift on the LCP.
  return (
    <div className="alice-carousel">
      <div>
        <div
          className="alice-carousel__wrapper"
          onTouchStart={ onTouchStart }
          onTouchEnd={ onTouchEnd }
          onClickCapture={ onClickCapture }
        >
          <ul className="alice-carousel__stage">
            {
              config.map((item, i) => (
                <li
                  key={ item.id }
                  ref={ (el) => { slides.current[i] = el; } }
                  className={ `alice-carousel__stage-item${ i === selected ? ' __active' : '' }` }
                  aria-hidden={ i === selected ? undefined : true }
                >
                  <a
                    onDragStart={ handleDragStart }
                    onDragEnd={ handleDragStart }
                    href={ item.href }
                    title={ item.name }
                    tabIndex={ i === selected ? undefined : -1 }
                  >
                    { renderPicture(item.img, item.name) }
                    { renderPicture(item.svg, item.name) }
                  </a>
                </li>
              ))
            }
          </ul>
        </div>
      </div>
      <ul className="alice-carousel__dots">
        {
          config.map((item, i) => (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <li
              key={ item.id }
              className={ `alice-carousel__dots-item${ i === index ? ' __active' : '' }` }
              onClick={ () => goTo(i) }
            />
          ))
        }
      </ul>
      {
        showArrows && (
          <div className="alice-carousel__prev-btn">
            <button type="button" onClick={ () => goTo(index - 1) }>
              <img src={ left } alt="previous" />
            </button>
          </div>
        )
      }
      {
        showArrows && (
          <div className="alice-carousel__next-btn">
            <button type="button" onClick={ () => goTo(index + 1) }>
              <img src={ right } alt="next" />
            </button>
          </div>
        )
      }
    </div>
  );
};

HpPromoV2.propTypes = {
  config: PropTypes.array.isRequired,
  showArrows: PropTypes.bool,
  interval: PropTypes.number
};

HpPromoV2.defaultProps = {
  showArrows: true,
  interval: AUTOPLAY_INTERVAL
};

export default HpPromoV2;
